/**
 * Scottish Gaelic catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Scottish Gaelic (Gàidhlig) at a mathematician's register. **Warrant →
 * «Barantas»** (native term for warrant). CLDR cardinal categories are one / two / few / other
 * (one 1 & 11, two 2 & 12, few 3–10 & 13–19, other 0 & 20+), so `frameActive` declares exactly
 * those (plus an explicit `=0`).
 *
 * NOTE (honesty over guessing): Gaelic numeral–noun agreement is intricate; the inflected
 * `frameActive` forms and several technical coinages are best-effort and are flagged
 * "expert-review suggested" in `Agent9_REPORT.md`. W/M grades stay Latin
 * (`::numbering-system/latn`). Per INV-STABLE-ID any correction changes only the string.
 */
import { P2 } from '../message-ids-phase2.js';
export const GD_CATALOG = {
    locale: 'gd',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Rannsachadh',
        [P2.sectionClaims]: 'Tagraidhean',
        [P2.sectionIntegrity]: 'Iomlanachd',
        [P2.axisWarrantLabel]: 'Barantas',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirigeach',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Mìneachadh} interp {Eadar-mhìneachadh} testable {So-dhearbhaidh} tested {Dearbhte} refuted {Breugnaichte} program {Prògram rannsachaidh} philos {Feallsanachail} other {Neo-aithnichte}}',
        [P2.axisModalLabel]: 'Mòdach',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Ìre neairt {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'So-rannsachaidh',
        [P2.stateRefuted]: 'Breugnaichte',
        [P2.stateOutside]: 'Taobh a-muigh na sgòip',
        [P2.verdictConflict]: 'An aghaidh {constraints}',
        [P2.verdictOutside]: 'Feumaidh {missing}',
        [P2.lossRegisterPlain]: 'Simplichte gu cànan àbhaisteach — tha cuid den phongalachd air fhàgail às.',
        [P2.lossRegisterKid]: 'Simplichte do luchd-leughaidh òga — tha mòran mhion-fhiosrachaidh air fhàgail às.',
        [P2.lossProfileDetail]: "Tha a' phròifil iomlan Barantas / Empirigeach / Mòdach air a gheàrr-chunntas an seo.",
        [P2.lossConflictDetail]: "Tha mion-fhiosrachadh na còmhstri air a ghiorrachadh san t-sealladh seo.",
        [P2.laneVerified]: 'Dearbhte',
        [P2.laneUnverified]: 'Neo-dhearbhte',
        [P2.laneConflict]: 'Còmhstri dearbhte',
        [P2.constraintLogic]: 'Loidsig',
        [P2.constraintLightBends]: 'Lùbaidh solas ann an raon iom-tharraing',
        [P2.constraintParallelPostulate]: 'Postalachd nan loidhnichean co-shìnte',
        [P2.claimScalarGravity]: 'Teòiridh sgalarach an iom-tharraing',
        [P2.claimNonEuclidean]: 'Geoimeatraidh neo-Euclideach',
        [P2.claimSubstrateSr]: 'Eadar-mhìneachadh fo-stratais air a’ choibhneasachd shònraichte',
        [P2.edgeExtObservation]: 'Amharc bhon taobh a-muigh',
        [P2.edgeResourceSample]: 'Goireas coimpiutaireachd co-roinnte',
        [P2.edgeHeuristicSample]: 'Stiùireadh heuristeach',
        [P2.edgeSharedSample]: 'Bunait cho-roinnte',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Bacaidhean',
        [P2.sectionEdges]: 'Oirean',
        [P2.sectionFrame]: 'Frèam',
        [P2.sectionExposure]: 'Foillseachadh',
        [P2.statusAdoptedFormal]: 'Gabhte (foirmeil)',
        [P2.statusAdoptedInterpretive]: 'Gabhte (eadar-mhìneachail)',
        [P2.statusSuspended]: 'Crochte',
        [P2.edgeEffectConstraint]: "A' dèanamh bacadh (faodaidh e breugnachadh)",
        [P2.edgeEffectResource]: 'Goireas — gun bhuaidh air breithean',
        [P2.edgeEffectHeuristic]: 'Heuristeach — gun bhuaidh air breithean',
        [P2.edgeEffectShared]: 'Bunait cho-roinnte — gun bhuaidh air breithean',
        [P2.frameActive]: '{count, plural, =0 {Gun bhacadh gnìomhach} one {# bhacadh ghnìomhach} two {# bhacadh ghnìomhach} few {# bacaidhean gnìomhach} other {# bacaidhean gnìomhach}}',
        [P2.exposureNone]: 'Gun eisimeileachdan eadar-mhìneachail',
        [P2.exposureDep]: 'An eisimeil {id}',
        [P2.laneTrackMachine]: 'Inneal',
        [P2.laneTrackExpert]: 'Eòlaiche',
        [P2.laneTrackCommunity]: 'Coimhearsnachd',
        [P2.expertAudited]: 'Sgrùdaichte',
        [P2.expertInProgress]: "A' dol air adhart",
        [P2.expertNotReviewed]: 'Gun ath-sgrùdadh',
        [P2.communityFlags]: 'Tha brataich ann',
        [P2.communityNone]: 'Gun bhrataich',
        [P2.controlChallenge]: 'Dùbhlan',
        [P2.controlActivate]: 'Gnìomhaich',
        [P2.controlMakeInterpretive]: 'Dèan eadar-mhìneachail',
        [P2.controlImportEdge]: 'Ion-phortaich oir',
        [P2.controlPromote]: 'Àrdaich',
        [P2.controlUndo]: 'Neo-dhèan',
        [P2.controlRedo]: 'Ath-dhèan',
        [P2.controlRegister]: 'Clàraich',
        [P2.promoteRejected]: 'Àrdachadh air a dhiùltadh',
        [P2.promoteAccepted]: 'Àrdachadh air a ghabhail',
        [P2.promoteReasonRefutationTerminal]: 'Chan urrainn dearbhadh breugnachadh a leigheas.',
        [P2.promoteReasonKeystone]: 'Feumaidh àrdachadh prìomh-chloiche taic empirigeach.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Chan eil an seòrsa empirigeach socraichte.',
        [P2.promoteReasonModalNotFixed]: 'Chan eil an ìre mhòdach socraichte.',
        [P2.promoteReasonWarrantNotImproved]: "Cha deach ìre a' bharantais a leasachadh.",
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=gd.js.map